import { useEffect, useMemo, useState } from "react";
import { BedDouble, CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Images, Loader2, MapPin, Pencil, Search, Star, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { API, api } from "@/api";
import { useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";

const photoUrl = (cityId, s) => (s.has_photo ? `${API}/public/stays/${cityId}/${s.place_id}/photo` : null);
const mapsUrl = (s) => `https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`;

const Stars = ({ rating }) => {
    const full = Math.round(rating || 0);
    return (
        <span className="inline-flex items-center gap-0.5" aria-label={`${rating}/5`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={12} className={n <= full ? "fill-amber-400 text-amber-400" : "text-stone-600"} />
            ))}
            <span className="ml-1.5 text-[10px] text-stone-400">{Number(rating).toFixed(1)}</span>
        </span>
    );
};

/** Lightbox a schermo intero: frecce, contatore, chiusura (anche Esc). */
const Lightbox = ({ photos, idx, onClose, onNav }) => {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") onNav(1);
            if (e.key === "ArrowLeft") onNav(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onNav]);
    if (idx === null) return null;
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/95 backdrop-blur-md" data-testid="stay-lightbox"
            onClick={onClose}>
            <button data-testid="lightbox-close" onClick={onClose} aria-label="close"
                className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                <X size={18} />
            </button>
            <span className="absolute left-5 top-6 font-mono-accent text-xs text-amber-400/90">{idx + 1} / {photos.length}</span>
            {photos.length > 1 && (
                <>
                    <button data-testid="lightbox-prev" aria-label="prev" onClick={(e) => { e.stopPropagation(); onNav(-1); }}
                        className="absolute left-3 top-1/2 z-10 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                        <ChevronLeft size={20} />
                    </button>
                    <button data-testid="lightbox-next" aria-label="next" onClick={(e) => { e.stopPropagation(); onNav(1); }}
                        className="absolute right-3 top-1/2 z-10 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                        <ChevronRight size={20} />
                    </button>
                </>
            )}
            <img src={photos[idx]} alt="" className="max-h-[86vh] max-w-[92vw] rounded-xl border border-amber-500/20 object-contain"
                onClick={(e) => e.stopPropagation()} />
        </motion.div>
    );
};

/**
 * Selezione alloggio per una tappa del Tour dell'Italia (design bozza premium).
 * Solo dati reali verificati (Google Places + cura del gestionale): nome,
 * indirizzo, posizione, valutazione ospiti, foto. MAI prezzi/stelle/servizi
 * inventati: il prezzo è comunicato nel preventivo finale.
 */
export const StayDialog = ({ open, onClose, cityId, cityName, leg, participants, stay, onSave, onRemove }) => {
    const { t } = useLang();
    const [rooms, setRooms] = useState(stay?.rooms || 1);
    const [stays, setStays] = useState(null);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [customNotes, setCustomNotes] = useState("");
    const [openingPartner, setOpeningPartner] = useState(false);
    const [detail, setDetail] = useState(null);
    const [gallery, setGallery] = useState(null);
    const [galleryIdx, setGalleryIdx] = useState(0);
    const [lightboxIdx, setLightboxIdx] = useState(null);

    const openDetail = (s) => {
        setDetail(s);
        setGallery(null);
        setGalleryIdx(0);
        api.get(`/public/stays/${cityId}/${s.place_id}/photos`)
            .then((r) => {
                const urls = (r.data.photos || []).map((p) => `${API.replace(/\/api$/, "")}${p}`);
                setGallery(urls.length ? urls : (photoUrl(cityId, s) ? [photoUrl(cityId, s)] : []));
            })
            .catch(() => setGallery(photoUrl(cityId, s) ? [photoUrl(cityId, s)] : []));
    };

    useEffect(() => {
        if (!open || !cityId) return undefined;
        setStays(null); setQ(""); setCat(""); setCustomNotes(""); setDetail(null); setGallery(null); setLightboxIdx(null);
        let alive = true;
        const params = cityId.startsWith("gplace_") ? { name: cityName } : {};
        api.get(`/public/stays/${cityId}`, { params })
            .then((r) => { if (alive) setStays(r.data.stays || []); })
            .catch(() => { if (alive) setStays([]); });
        return () => { alive = false; };
    }, [open, cityId, cityName]);

    const filtered = useMemo(() => (stays || []).filter((s) => {
        const text = `${s.name} ${s.address || ""}`.toLowerCase();
        if (q && !text.includes(q.toLowerCase())) return false;
        if (!cat) return true;
        const pl = s.price_level;
        if (cat === "luxury") return pl === "very_expensive" || pl === "expensive";
        if (cat === "mid") return pl === "moderate";
        if (cat === "value") return pl === "inexpensive" || pl === "free";
        return true;
    }), [stays, q, cat]);

    if (!open) return null;

    const checkin = leg?.from || null;
    const checkout = leg?.to || null;
    const guests = Number(participants) || 2;
    const hasDates = Boolean(checkin && checkout);
    const L = ({ children }) => <span className="text-[10px] uppercase tracking-[0.18em] text-amber-500/90 font-semibold">{children}</span>;

    const selectStay = (s) => {
        onSave({
            name: s.name, place_id: s.place_id, address: s.address || null, rating: s.rating || null,
            lat: s.lat ?? null, lng: s.lng ?? null,
            type: null, price: null, link: null,
            checkin, checkout, guests, rooms, status: "selected",
        });
        onClose();
    };

    const saveCustom = () => {
        onSave({
            name: `${t.builder.stay.customName} — ${cityName}`, type: "custom", notes: customNotes.trim() || null,
            price: null, link: null, checkin, checkout, guests, rooms, status: "selected",
        });
        onClose();
    };

    const openPartner = async () => {
        setOpeningPartner(true);
        try {
            const { data } = await api.post("/public/stays/partner-link", { city: cityName, checkin, checkout, guests, rooms });
            window.open(data.url, "_blank", "noopener");
        } catch {
            toast.error(t.booking.errorGeneric);
        } finally {
            setOpeningPartner(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/75 backdrop-blur-sm" data-testid="stay-dialog">
            <div className="mx-auto max-w-5xl px-4 py-10">
                <div className="rounded-2xl border border-amber-500/30 bg-[#111112] p-6 sm:p-8">
                    {/* HEADER */}
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h3 className="flex items-center gap-2.5 font-display text-2xl sm:text-3xl text-amber-50">
                                <BedDouble size={22} className="text-amber-400" /> {t.builder.stay.title.replace("{city}", cityName)}
                            </h3>
                            <p className="mt-1 text-xs text-stone-400">{t.builder.stay.listSub}</p>
                            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-stone-400" data-testid="stay-dates">
                                <CalendarDays size={12} className="text-amber-500/80" />
                                {hasDates ? `${checkin} → ${checkout} · ${guests} ${t.builder.stay.guests.toLowerCase()}` : t.builder.staySetDates}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button data-testid="stay-edit-dates" onClick={onClose}
                                className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-400/10">
                                <Pencil size={11} /> {t.builder.stay.editDates}
                            </button>
                            <button data-testid="stay-close" onClick={onClose} aria-label="close"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                                <X size={15} />
                            </button>
                        </div>
                    </div>

                    {/* FILTRI (nascosti nella vista dettaglio) */}
                    {!detail && (
                    <div className="mt-6 grid gap-3 sm:grid-cols-3">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-[#0b0b0c] px-3 py-2.5 sm:col-span-1">
                            <Search size={14} className="shrink-0 text-amber-500/70" />
                            <input data-testid="stay-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.builder.stay.searchNamePh}
                                className="w-full bg-transparent text-sm text-stone-200 placeholder:text-stone-500 focus:outline-none" />
                        </div>
                        {stays?.some((s) => s.price_level) && (
                            <label className="block"><L>{t.builder.stay.catLabel}</L>
                                <select data-testid="stay-category" className="input-lux mt-1" value={cat} onChange={(e) => setCat(e.target.value)}>
                                    <option value="">{t.builder.stay.catAll}</option>
                                    <option value="luxury">{t.builder.stay.catLuxury}</option>
                                    <option value="mid">{t.builder.stay.catMid}</option>
                                    <option value="value">{t.builder.stay.catValue}</option>
                                </select>
                            </label>
                        )}
                        <label className="block"><L>{t.builder.stay.rooms}</L>
                            <select data-testid="stay-rooms" className="input-lux mt-1" value={rooms} onChange={(e) => setRooms(Number(e.target.value))}>
                                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    </div>
                    )}

                    {/* VISTA DETTAGLIO STRUTTURA — galleria fotografica */}
                    {detail && (() => {
                        const photos = gallery ?? (photoUrl(cityId, detail) ? [photoUrl(cityId, detail)] : []);
                        const isSel = stay?.place_id && stay.place_id === detail.place_id;
                        return (
                            <div className="mt-6" data-testid="stay-detail">
                                <button data-testid="stay-detail-back" onClick={() => { setDetail(null); setLightboxIdx(null); }}
                                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-stone-400 transition-colors hover:text-amber-300">
                                    <ChevronLeft size={14} /> {t.builder.stay.backToResults}
                                </button>

                                <div className="mt-4">
                                    {photos.length > 0 ? (
                                        <>
                                            <button className="relative block w-full overflow-hidden rounded-2xl border border-amber-500/25" onClick={() => setLightboxIdx(galleryIdx)} data-testid="stay-detail-main">
                                                <GoldImage src={photos[galleryIdx]} alt={detail.name} className="h-64 w-full object-cover sm:h-80" />
                                                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-200 backdrop-blur-sm">
                                                    <Images size={11} /> {galleryIdx + 1} / {photos.length}
                                                </span>
                                            </button>
                                            {photos.length > 1 && (
                                                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="stay-detail-thumbs">
                                                    {photos.map((p, i) => (
                                                        <button key={i} data-testid={`stay-thumb-${i}`} onClick={() => setGalleryIdx(i)}
                                                            className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${i === galleryIdx ? "border-amber-400" : "border-transparent opacity-60 hover:opacity-100"}`}>
                                                            <img src={p} alt="" loading="lazy" className="h-full w-full object-cover" />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="mt-2 text-[10px] text-stone-600">{t.builder.stay.photosCredit}</p>
                                        </>
                                    ) : (
                                        <p className="rounded-xl border border-amber-500/20 bg-[#0b0b0c] p-6 text-center text-sm text-stone-400" data-testid="stay-detail-nophotos">{t.builder.stay.noMorePhotos}</p>
                                    )}
                                    {gallery && gallery.length <= 1 && photos.length > 0 && (
                                        <p className="mt-2 text-[11px] text-stone-500" data-testid="stay-detail-nomore">{t.builder.stay.noMorePhotos}</p>
                                    )}
                                </div>

                                <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <h3 className="font-display text-2xl text-amber-50">{detail.name}</h3>
                                        <div className="mt-1.5"><Stars rating={detail.rating} /></div>
                                        <p className="mt-1 text-[10px] text-stone-500">{t.builder.stay.ratingSuffix.replace("{n}", (detail.reviews || 0).toLocaleString("it-IT"))}</p>
                                        {detail.address && (
                                            <p className="mt-2 flex items-start gap-1.5 text-xs text-stone-400">
                                                <MapPin size={12} className="mt-0.5 shrink-0 text-amber-500/70" /> {detail.address}
                                            </p>
                                        )}
                                        <a data-testid="stay-detail-map" href={mapsUrl(detail)} target="_blank" rel="noopener noreferrer"
                                            className="mt-1.5 inline-flex items-center gap-1 text-xs text-amber-300 underline decoration-amber-500/40 underline-offset-2 hover:text-amber-200">
                                            <MapPin size={12} /> {t.builder.stay.viewMap}
                                        </a>
                                    </div>
                                    {isSel ? (
                                        <button data-testid="stay-detail-unselect" onClick={() => { onRemove(); onClose(); }}
                                            className="rounded-full border border-red-500/40 px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-red-300 transition-colors hover:bg-red-500/10">
                                            {t.builder.stay.removeSel}
                                        </button>
                                    ) : (
                                        <button data-testid="stay-detail-select" onClick={() => selectStay(detail)}
                                            className="rounded-full bg-amber-400 px-7 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(241,211,108,0.4)]">
                                            {t.builder.stay.select}
                                        </button>
                                    )}
                                </div>

                                <p className="mt-4 rounded-lg border border-amber-500/20 bg-amber-400/5 p-3 text-[11px] leading-relaxed text-stone-400" data-testid="stay-detail-pricenote">
                                    {t.builder.stay.priceNote}
                                </p>

                                <div className="mt-4 rounded-xl border border-amber-500/20 bg-[#0b0b0c] p-5" data-testid="stay-detail-rooms">
                                    <p className="text-sm font-semibold text-amber-100">{t.builder.stay.roomsTitle}</p>
                                    <p className="mt-2 text-[11px] leading-relaxed text-stone-400">{t.builder.stay.roomsNote}</p>
                                </div>
                            </div>
                        );
                    })()}

                    {/* SCHEDE STRUTTURE (nascoste nella vista dettaglio) */}
                    {!detail && (stays === null ? (
                        <div className="flex flex-col items-center py-16" data-testid="stay-loading">
                            <Loader2 size={26} className="animate-spin text-amber-400" />
                        </div>
                    ) : filtered.length > 0 ? (
                        <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="stay-results">
                            {filtered.map((s) => {
                                const isSel = stay?.place_id && stay.place_id === s.place_id;
                                return (
                                    <div key={s.place_id} data-testid={`stay-card-${s.place_id}`}
                                        className={`gold-card flex flex-col overflow-hidden rounded-xl ${isSel ? "!border-amber-400/80 shadow-[0_0_24px_rgba(214,180,92,0.18)]" : ""}`}>
                                        <div className="relative h-44 overflow-hidden cursor-pointer" onClick={() => openDetail(s)} data-testid={`stay-photos-${s.place_id}`} role="button" aria-label={t.builder.stay.viewPhotos}>
                                            <GoldImage src={photoUrl(cityId, s)} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                                            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/65 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-amber-200 backdrop-blur-sm">
                                                <Images size={10} /> {t.builder.stay.viewPhotos}
                                            </span>
                                            {isSel && (
                                                <span className="absolute right-3 top-3 rounded-full bg-amber-400 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-black">
                                                    {t.builder.stay.selected} ✓
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-1 flex-col p-4">
                                            <h4 className="cursor-pointer font-display text-base text-amber-50 leading-snug hover:text-amber-300 transition-colors" onClick={() => openDetail(s)} data-testid={`stay-name-${s.place_id}`}>{s.name}</h4>
                                            <div className="mt-1.5"><Stars rating={s.rating} /></div>
                                            <p className="mt-1 text-[10px] text-stone-500">{t.builder.stay.ratingSuffix.replace("{n}", (s.reviews || 0).toLocaleString("it-IT"))}</p>
                                            {s.address && (
                                                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-stone-400">
                                                    <MapPin size={11} className="mt-0.5 shrink-0 text-amber-500/70" /> {s.address}
                                                </p>
                                            )}
                                            <a data-testid={`stay-map-${s.place_id}`} href={mapsUrl(s)} target="_blank" rel="noopener noreferrer"
                                                className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-300 underline decoration-amber-500/40 underline-offset-2 hover:text-amber-200">
                                                <MapPin size={11} /> {t.builder.stay.viewMap}
                                            </a>
                                            <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-400/5 p-2.5 text-[10px] leading-relaxed text-stone-400" data-testid={`stay-pricenote-${s.place_id}`}>
                                                {t.builder.stay.priceNote}
                                            </p>
                                            <div className="mt-3 flex-1" />
                                            {isSel ? (
                                                <button data-testid={`stay-unselect-${s.place_id}`} onClick={() => { onRemove(); onClose(); }}
                                                    className="w-full rounded-full border border-red-500/40 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-red-300 transition-colors hover:bg-red-500/10">
                                                    {t.builder.stay.removeSel}
                                                </button>
                                            ) : (
                                                <button data-testid={`stay-select-${s.place_id}`} onClick={() => selectStay(s)}
                                                    className="w-full rounded-full bg-amber-400 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-amber-300">
                                                    {t.builder.stay.select}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="mt-6 rounded-xl border border-amber-500/20 bg-[#0b0b0c] p-6 text-center" data-testid="stay-empty">
                            <p className="text-sm text-stone-400">{t.builder.stay.emptyCatalog}</p>
                            <button data-testid="stay-partner" onClick={openPartner} disabled={openingPartner}
                                className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-500/50 px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:opacity-50">
                                {openingPartner ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                                {t.builder.stay.partnerFallback}
                            </button>
                        </div>
                    ))}

                    {/* PROPOSTA PERSONALIZZATA */}
                    <div className="mt-6 rounded-xl border border-amber-500/20 bg-[#0b0b0c] p-5" data-testid="stay-custom">
                        <p className="text-sm font-semibold text-amber-100">{t.builder.stay.noFit}</p>
                        <textarea data-testid="stay-custom-notes" rows={2} value={customNotes} onChange={(e) => setCustomNotes(e.target.value)}
                            placeholder={t.builder.stay.customPh}
                            className="input-lux mt-3 w-full resize-none text-sm" />
                        <div className="mt-3 flex flex-wrap gap-3">
                            <button data-testid="stay-custom-save" onClick={saveCustom}
                                className="rounded-full bg-amber-400 px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-black transition-colors hover:bg-amber-300">
                                {t.builder.stay.customCta}
                            </button>
                            <button data-testid="stay-skip" onClick={onClose}
                                className="rounded-full border border-amber-500/40 px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                {t.builder.stay.skip}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {lightboxIdx !== null && gallery?.length > 0 && (
                    <Lightbox
                        photos={gallery}
                        idx={lightboxIdx}
                        onClose={() => setLightboxIdx(null)}
                        onNav={(d) => setLightboxIdx((i) => (i + d + gallery.length) % gallery.length)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};
